import type { AccountOption } from "@app/components/account-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import TxnTablePanel, { useTxnSort } from "@app/components/txn-table-panel";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { monthDetailTxns_Query } from "@app/queries/monthDetailTxns_Query.graphql";

// One month of transactions on the shared editable shell, pulled straight off the
// unified `transactions(month: …)` resolver (which returns dollars, not cents), plus
// the option lists the shared bulk-action registry needs.
const txnsQuery = graphql`
  query monthDetailTxns_Query(
    $month: String
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      month: $month
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

export default function MonthDetail({
  month,
  onClose,
}: {
  month: string;
  onClose: () => void;
}) {
  return (
    <div className="bg-white rounded border">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 className="font-bold">{month}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-gray-500 hover:text-gray-900"
        >
          Close
        </button>
      </div>

      <Suspense
        fallback={
          <p className="p-6 text-sm text-gray-400">Loading transactions…</p>
        }
      >
        <MonthTxns month={month} />
      </Suspense>
    </div>
  );
}

// The editable transactions list for one month, fed by its own
// `transactions(month: …)` query and wired to the shared bulk-action registry.
function MonthTxns({ month }: { month: string }) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<monthDetailTxns_Query>(
    txnsQuery,
    {
      month,
      perspectiveId: getPerspectiveId() ?? undefined,
      ...sortVars,
    },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] =
    data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: () => refresh(),
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  const exportParams: Record<string, string> = {
    month,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <>
      <TxnTablePanel
        mode="inline"
        interaction="editable"
        title="Transactions"
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="No transactions in this month."
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        renderBulkActions={renderBulkActions}
      />
      {dialogs}
    </>
  );
}
